import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { usePublicCompanySettings } from '@/hooks/usePublicCompanySettings';
import { trackResultsViewed } from '@/utils/amplitudeEvents';
import ResultsDashboard from '@/components/ResultsDashboard';
import SimpleConfirmation from '@/components/thank-you/SimpleConfirmation';
import { CompanyBehavioralResultsView, CompanyFullResultsView } from '@/components/results/views';

// Default company for general/platform candidates (self-directed assessments)
const ZENTRIX_INSIGHTS_COMPANY_ID = '87090009-e2e5-4b96-af27-a8b5691abf85';

type AssessmentType = 
  | 'self-directed'      // Full dashboard (no company)
  | 'company-behavioral' // Single chart + AI (behavioral only, company)
  | 'company-full'       // Dual charts + AI (behavioral + cognitive, company)
  | 'cognitive-only';    // Always SimpleConfirmation

const ResultsPage = () => {
  const navigate = useNavigate();
  const { submissionId } = useParams();
  const { toast } = useToast();
  const { user, checkAdminStatus } = useAuth();
  
  const [assessmentType, setAssessmentType] = useState<AssessmentType | null>(null);
  const [behavioralData, setBehavioralData] = useState<any>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [adminStatusLoading, setAdminStatusLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // Fetch company visibility setting
  const { showResults, isLoading: isLoadingSettings } = usePublicCompanySettings(companyId);

  // Animation visibility
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Check admin status when user is available
  useEffect(() => {
    const loadAdminStatus = async () => {
      if (user) {
        console.log('ResultsPage: User authenticated, checking admin status for:', user.email);
        try {
          await checkAdminStatus();
        } catch (error) {
          console.error('ResultsPage: Error checking admin status:', error);
        }
      }
      setAdminStatusLoading(false);
    };

    loadAdminStatus();
  }, [user, checkAdminStatus]);

  // Main detection logic
  useEffect(() => {
    const detectAndLoadAssessment = async () => {
      console.log('ResultsPage: Detecting assessment type...', { submissionId });

      if (!submissionId) {
        console.error('ResultsPage: No submission ID provided');
        toast({
          title: "Results not available",
          description: "No assessment submission found. Please complete an assessment first.",
          variant: "destructive"
        });
        navigate('/assessment');
        return;
      }

      try {
        // Step 1: Try fetching from survey_submissions (behavioral)
        console.log('ResultsPage: Checking survey_submissions for ID:', submissionId);
        const { data: behavioralRows, error: behavioralError } = await supabase.rpc('get_submission_by_id', {
          p_submission_id: submissionId
        });

        if (behavioralError) {
          console.error('ResultsPage: Error fetching behavioral submission:', behavioralError);
        }

        const behavioralSubmission = Array.isArray(behavioralRows) && behavioralRows.length > 0 
          ? behavioralRows[0] 
          : null;

        if (behavioralSubmission) {
          console.log('ResultsPage: Found behavioral submission', {
            id: behavioralSubmission.id,
            companyId: behavioralSubmission.company_id,
            email: behavioralSubmission.email
          });

          setBehavioralData(behavioralSubmission);
          setCompanyId(behavioralSubmission.company_id);

          // EVENT 11: Results Viewed
          trackResultsViewed({
            assessment_type: behavioralSubmission.assessment_type || 'behavioral',
            results_id: submissionId,
            company_id: behavioralSubmission.company_id,
            viewed_by_user_id: user?.id || null,
            is_own_result: user?.email === behavioralSubmission.email,
            user_email: behavioralSubmission.email || null,
            user_name: behavioralSubmission.full_name || null,
            profile_name: behavioralSubmission.profile_name || null,
            disc_code: behavioralSubmission.disc_code || null,
          });

          let hasCognitiveCompanion = false;
          if (!behavioralSubmission.company_id || 
              behavioralSubmission.company_id === ZENTRIX_INSIGHTS_COMPANY_ID) {
            // Self-directed assessment (no company or default tenant) - show full dashboard
            console.log('ResultsPage: Self-directed assessment detected');
            setAssessmentType('self-directed');
          } else {
            // Company-linked - check for cognitive companion
            console.log('ResultsPage: Company-linked assessment, checking for cognitive...');
            const { data: cognitiveData } = await supabase
              .from('cognitive_submissions')
              .select('id')
              .eq('email', behavioralSubmission.email)
              .eq('company_id', behavioralSubmission.company_id)
              .is('deleted_at', null)
              .maybeSingle();

            if (cognitiveData) {
              hasCognitiveCompanion = true;
              console.log('ResultsPage: Full assessment detected (behavioral + cognitive)');
              setAssessmentType('company-full');
            } else {
              console.log('ResultsPage: Behavioral-only assessment detected');
              setAssessmentType('company-behavioral');
            }
          }
          
          setIsLoading(false);
          return;
        }

        // Step 2: Not found in behavioral - try cognitive_submissions
        console.log('ResultsPage: Not found in behavioral, checking cognitive_submissions...');
        const { data: cognitiveRows, error: cognitiveError } = await supabase.rpc('get_cognitive_submission_by_id', {
          p_submission_id: submissionId
        });

        if (cognitiveError) {
          console.error('ResultsPage: Error fetching cognitive submission:', cognitiveError);
        }

        const cognitiveSubmission = Array.isArray(cognitiveRows) && cognitiveRows.length > 0 
          ? cognitiveRows[0] 
          : null;

        if (cognitiveSubmission) {
          console.log('ResultsPage: Found cognitive-only submission');
          setCompanyId(cognitiveSubmission.company_id);
          setAssessmentType('cognitive-only');
          setIsLoading(false);
          return;
        }

        // Step 3: Not found anywhere
        console.error('ResultsPage: No submission found for ID:', submissionId);
        toast({
          title: "Results not found",
          description: "The assessment results could not be found. They may have been deleted.",
          variant: "destructive"
        });
        navigate('/assessment');

      } catch (error) {
        console.error('ResultsPage: Unexpected error:', error);
        toast({
          title: "Something went wrong",
          description: "We couldn't load your results. Please try again or contact support.",
          variant: "destructive"
        });
        navigate('/assessment');
      }
    };

    detectAndLoadAssessment();
  }, [navigate, submissionId, toast]);

  const handleBackToHome = () => {
    console.log('ResultsPage: Clearing results and navigating to home');
    localStorage.removeItem('assessmentResults');
    localStorage.removeItem('dualAssessmentResults');
    localStorage.removeItem('lastSubmissionId');
    navigate('/');
  };

  // Loading state
  if (isLoading || adminStatusLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-6 w-6 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  // Wait for company settings to load for company-linked assessments
  if (companyId && isLoadingSettings) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-6 w-6 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  // Self-directed → full dashboard
  if (assessmentType === 'self-directed' && behavioralData) {
    // Transform data for ResultsDashboard
    const scoresObj = behavioralData.scores;
    
    // Check if this is dual assessment data
    if (scoresObj && typeof scoresObj === 'object' && 
        'selfBehavior' in scoresObj && 'workBehavior' in scoresObj) {
      const selfBehavior = scoresObj.selfBehavior as any;
      const workBehavior = scoresObj.workBehavior as any;
      
      const selectedWordsObj = behavioralData.selected_words && typeof behavioralData.selected_words === 'object' && !Array.isArray(behavioralData.selected_words)
        ? behavioralData.selected_words as Record<string, any> : {};
      const selectedWordsTextObj = behavioralData.selected_words_text && typeof behavioralData.selected_words_text === 'object' && !Array.isArray(behavioralData.selected_words_text)
        ? behavioralData.selected_words_text as Record<string, any> : {};

      const dualData = {
        selfBehavior: {
          ...(selfBehavior || {}),
          selectedWords: (selectedWordsObj.self as any[]) || [],
          selectedWordsText: (selectedWordsTextObj.self as any[]) || []
        },
        workBehavior: {
          ...(workBehavior || {}),
          selectedWords: (selectedWordsObj.work as any[]) || [],
          selectedWordsText: (selectedWordsTextObj.work as any[]) || []
        },
        // DB profile_name is based on self behavior for dual assessments
        selfProfileName: behavioralData.profile_name || null,
        completedAt: behavioralData.completed_at,
        submissionId: behavioralData.id,
        fullName: behavioralData.full_name,
        email: behavioralData.email,
        companyId: behavioralData.company_id,
        isDualAssessment: true,
        candidateLanguage: (behavioralData.assessment_metadata as any)?.language || 'en',
        assessmentVariant: behavioralData.assessment_variant,
        durationSeconds: (behavioralData.assessment_metadata as any)?.duration_seconds ?? null,
      };

      return (
        <ResultsDashboard 
          data={dualData as any} 
          onBackToHome={handleBackToHome}
        />
      );
    }
    
    // Single assessment format
    const transformedData = {
      scores: behavioralData.scores,
      selectedWordsText: behavioralData.selected_words_text,
      profileName: behavioralData.profile_name,  // DB source of truth for profile type display
      profile_name: behavioralData.profile_name, // alias for ResultsDashboard
      completedAt: behavioralData.completed_at,
      submissionId: behavioralData.id,
      fullName: behavioralData.full_name,
      email: behavioralData.email,
      companyId: behavioralData.company_id,
      candidateLanguage: (behavioralData.assessment_metadata as any)?.language || 'en',
      assessmentVariant: behavioralData.assessment_variant,
      durationSeconds: (behavioralData.assessment_metadata as any)?.duration_seconds ?? null,
    };

    return (
      <ResultsDashboard 
        data={transformedData as any} 
        onBackToHome={handleBackToHome}
      />
    );
  }

  // Cognitive-only → always SimpleConfirmation (scores never shown)
  if (assessmentType === 'cognitive-only') {
    return <SimpleConfirmation isVisible={isVisible} />;
  }

  // Company behavioral/full → check toggle
  if ((assessmentType === 'company-behavioral' || assessmentType === 'company-full') && companyId) {
    // Toggle is OFF → show simple confirmation
    if (!showResults) {
      return <SimpleConfirmation isVisible={isVisible} />;
    }

    // Toggle is ON → show appropriate view
    if (assessmentType === 'company-behavioral') {
      return <CompanyBehavioralResultsView behavioralData={behavioralData} isVisible={isVisible} />;
    }

    if (assessmentType === 'company-full') {
      return <CompanyFullResultsView behavioralData={behavioralData} isVisible={isVisible} />;
    }
  }

  // Fallback - no data available
  console.log('ResultsPage: No assessment data available, redirecting...');
  navigate('/');
  return null;
};

export default ResultsPage;

